import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import moment from 'moment';
import { DailyForecast, WeeklyWeather } from '../components/widgets/weather-widget/models/weather.model';
import { TempUnit, WeatherHourlyForecast, WeatherQueryResponse, WeatherRequestInput, WeatherWidgetData } from '../models/weather-widget.model';
import { WEATHER_QUERY } from '../shared/graphql/weather.query';
import { ApiEndpointsService } from './api-endpoints.service';
// import { MOCK_WEATHER_FORECAST } from '../shared/mocks/weather-forecast.mock';

@Injectable({
  providedIn: 'root',
})
export class WeatherService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiEndpointsService);
  private readonly MAX_FORECAST_DAYS = 5;

  public getForecast(input: WeatherRequestInput): Observable<WeeklyWeather | null> {
    if (!input.location_id) {
      console.warn('location_id is required for weather forecast');
      return of(null);
    }

    const query = WEATHER_QUERY;
    return this.http.post<WeatherQueryResponse>(this.apiService.baseUrl, { query, variables: input }, { withCredentials: true }).pipe(
      map((response) => {
        const data = response?.data?.forecast;
        if (!data) {
          console.warn('No forecast data in response');
          return null;
        }
        return this.getWeeklyWeather(data);
      }),
      catchError((err) => {
        console.error('Failed to load weather data', err);
        return of<null>(null);
      })
    );
  }

  private getWeeklyWeather(data: WeatherWidgetData, city?: string, country?: string): WeeklyWeather {
    let days = this.mapDailyForecasts(data);
    // Limit to max forecast days
    days = days.slice(0, this.MAX_FORECAST_DAYS);
    // Transform to WeeklyWeather structure
    return this.transformWeeklyWeather(data, days, city, country);
  }

  private mapDailyForecasts(data: WeatherWidgetData): DailyForecast[] {
    return data.dailyForecast.map((day) => {
      const date = moment.parseZone(day.date).startOf('day').add(1, 'second');
      const dayEnd = moment(date).endOf('day').add(1, 'second');
      return {
        date: day.date,
        summaryCode: this.extractWeatherSummaryCode(day.iconUrl),
        condition: day.condition,
        iconUrl: day.iconUrl,
        minTemperature: day.minTemp,
        maxTemperature: day.maxTemp,
        hourly: this.filterHourlyData(data.selectedDayHourly, date, dayEnd),
      };
    });
  }

  private filterHourlyData(hourly: WeatherHourlyForecast[], dayStart: moment.Moment, dayEnd: moment.Moment) {
    return hourly
      .filter((hour) => {
        const hourTime = moment.parseZone(hour.time);
        return hourTime.isSameOrAfter(dayStart) && hourTime.isSameOrBefore(dayEnd);
      })
      .map((daily) => ({
        time: daily.time,
        temp: daily.temp,
        precipMm: 0,
        windKph: 0,
      }));
  }



  private transformWeeklyWeather(data: WeatherWidgetData, days: DailyForecast[], city?: string, country?: string): WeeklyWeather {
    return {
      city: city || '',
      country: country,
      currentTempUnit: data.unit as TempUnit,
      days: days,
    };
  }

  private extractWeatherSummaryCode(iconUrl: string): number | null {
    // The regular expression to find and capture the 2-digit code.
    const regex = /\/(\d{2})(?:-s)?\.png$/;

    // Execute the regex against the input string.
    const match = iconUrl.match(regex);

    if (match && match[1]) {
      // Parse the captured string ("02") into a base-10 integer.
      return parseInt(match[1], 10);
    }

    // If no match was found, return null to indicate failure.
    return null;
  }
}
